import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, ButtonDropdown, SpaceBetween, Spinner, Tabs } from '@amzn/awsui-components-react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import * as XLSX from 'xlsx';

/**
 * EmbedExcel - Standalone component that fetches an Excel file from a presigned URL
 * and renders each sheet in ag-grid with tabs for multi-sheet workbooks.
 *
 * Props:
 *   fileUrl (string)  - presigned S3 URL to the .xlsx/.xls file
 *   fileName (string) - original file name (used for export naming)
 *   onError (func)    - optional callback on load failure
 */
const EmbedExcel = ({ fileUrl, fileName = 'output.xlsx', onError }) => {
  const gridRef = useRef();
  const [sheetNames, setSheetNames] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [parsedSheets, setParsedSheets] = useState({});
  const [columnDefs, setColumnDefs] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [gridInfo, setGridInfo] = useState({ rowCount: 0, totalRows: 0 });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const defaultColDef = {
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: false,
    filterParams: { applyMiniFilterWhileTyping: true },
    headerClass: 'header-center',
    enableRowGroup: true,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
  };

  const sideBar = {
    toolPanels: [
      { id: 'columns', labelDefault: 'Columns', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' },
      { id: 'filters', labelDefault: 'Filters', labelKey: 'filters', iconKey: 'filter', toolPanel: 'agFiltersToolPanel' },
    ],
    defaultToolPanel: '',
  };

  // Fetch and parse the Excel file when fileUrl changes
  useEffect(() => {
    if (!fileUrl) return;
    loadExcelFile(fileUrl);
  }, [fileUrl]);

  // When active sheet changes, update grid data
  useEffect(() => {
    if (activeSheet && parsedSheets[activeSheet]) {
      const { columns, rows } = parsedSheets[activeSheet];
      setColumnDefs(columns);
      setRowData(rows);
    }
  }, [activeSheet, parsedSheets]);

  const loadExcelFile = async (url) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch file: ${response.statusText}`);
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      const sheets = {};
      const validSheetNames = [];
      workbook.SheetNames.forEach((name) => {
        const worksheet = workbook.Sheets[name];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        // Skip sheets with no rows or only 1 row (likely metadata/cache sheets)
        if (jsonData.length < 2) return;

        const headers = Object.keys(jsonData[0]);
        const columns = headers.map((col, idx) => ({
          field: col,
          headerName: col,
          resizable: true,
          sortable: true,
          enableRowGroup: true,
          editable: false,
          ...(idx === 0 ? { checkboxSelection: true, headerCheckboxSelection: true } : {}),
        }));

        sheets[name] = { columns, rows: jsonData };
        validSheetNames.push(name);
      });

      setParsedSheets(sheets);
      setSheetNames(validSheetNames);
      setActiveSheet(validSheetNames[0] || null);
    } catch (err) {
      const msg = err?.message || 'Failed to load Excel file';
      setErrorMsg(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  const onGridReady = useCallback(() => {
    const allColumnIds = [];
    gridRef?.current?.api?.getColumns()?.forEach((column) => {
      allColumnIds.push(column.getId());
    });
    gridRef?.current?.api?.autoSizeColumns(allColumnIds);
    updateGridInfo();
  }, []);

  const updateGridInfo = () => {
    if (gridRef?.current?.api) {
      const api = gridRef.current.api;
      setGridInfo({
        rowCount: api.getDisplayedRowCount(),
        totalRows: rowData?.length || 0,
      });
    }
  };

  const exportData = (args = {}) => {
    const baseName = fileName.replace(/\.(xlsx|xls)$/i, '');
    const exportName = sheetNames.length > 1 ? `${baseName}_${activeSheet}.csv` : `${baseName}.csv`;
    gridRef.current?.api.exportDataAsCsv({
      fileName: exportName,
      exportedRows: args.filtered ? 'filteredAndSorted' : 'all',
      onlySelected: args.selectedOnly,
    });
  };

  const tabs = sheetNames.map((name) => ({
    id: name,
    label: name,
    content: <div />,
  }));

  if (loading) {
    return <Box padding="l"><Spinner /> Loading Excel file...</Box>;
  }

  if (errorMsg) {
    return <Box padding="l" color="text-status-error">{errorMsg}</Box>;
  }

  if (sheetNames.length === 0) {
    return null;
  }

  return (
    <SpaceBetween direction="vertical">
      {sheetNames.length > 1 && (
        <Tabs
          onChange={({ detail }) => setActiveSheet(detail.activeTabId)}
          activeTabId={activeSheet}
          tabs={tabs}
          disableContentPaddings={true}
        />
      )}
      <Box float="right" padding="xs">
        <ButtonDropdown
          items={[
            {
              text: 'Export to CSV',
              items: [
                { text: 'Current view', id: 'current_view' },
                { text: 'Selected rows', id: 'selected_rows' },
                { text: 'All data', id: 'all' },
              ],
            },
          ]}
          onItemClick={(event) => {
            if (event.detail.id === 'current_view') exportData({ filtered: true });
            else if (event.detail.id === 'selected_rows') exportData({ selectedOnly: true });
            else if (event.detail.id === 'all') exportData();
          }}
        >
          Actions
        </ButtonDropdown>
      </Box>
      <div style={{ width: '100%', height: 'calc(100vh - 200px)' }}>
        <div className="ag-theme-alpine" style={{ width: '100%', height: '100%', minHeight: '500px' }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            defaultColDef={defaultColDef}
            columnDefs={columnDefs}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            rowMultiSelectWithClick={true}
            animateRows={true}
            rowHeight={30}
            enableCellTextSelection={true}
            onGridReady={onGridReady}
            rowGroupPanelShow="always"
            sideBar={sideBar}
            enableRangeSelection={true}
            onFilterChanged={updateGridInfo}
            onViewportChanged={updateGridInfo}
          />
          {gridInfo && (
            <Box float="left" padding="xs">
              <SpaceBetween direction="horizontal" size="s">
                <Box><span className="agg-text bold-text">Filtered rows:</span> <span className="agg-text">{gridInfo.rowCount}</span></Box>
                <Box><span className="agg-text bold-text">Total rows:</span> <span className="agg-text">{gridInfo.totalRows}</span></Box>
              </SpaceBetween>
            </Box>
          )}
        </div>
      </div>
    </SpaceBetween>
  );
};

export default EmbedExcel;
